/**
 * CellDetailModal Component
 * 
 * Modal for viewing full cell content with type-specific rendering:
 * - String: Full text with copy button
 * - Object/Array: Formatted JSON with syntax highlighting
 * - GeoPoint: Coordinates + Google Maps link
 * - File: Preview (if image) + download button
 * - ACL: Permission breakdown table
 * - Polygon: List of coordinates
 */

import { useEffect, useRef, useCallback } from 'react';
import { 
  ParseFieldType, 
  ParseGeoPoint, 
  ParseFile, 
  ParseACL,
  ParsePolygon
} from '../../../../types/AppPanel';

interface CellDetailModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** The cell value to display */
  value: unknown;
  /** Field name */
  field: string;
  /** Parse field type */
  fieldType: ParseFieldType;
  /** Callback to close the modal */
  onClose: () => void;
}

export default function CellDetailModal({
  isOpen,
  value,
  field,
  fieldType,
  onClose,
}: CellDetailModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Close on click outside
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  // Copy to clipboard
  const copyToClipboard = useCallback(() => {
    const text = typeof value === 'object' 
      ? JSON.stringify(value, null, 2) 
      : String(value);
    navigator.clipboard.writeText(text);
  }, [value]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className="relative w-full max-w-2xl max-h-[80vh] m-4 
                   bg-[#0c121c] rounded-xl border border-white/10 
                   shadow-2xl overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-white/5">
          <div className="flex items-center gap-2">
            <span className="text-white font-medium">{field}</span>
            <span className="text-white/40 text-sm px-2 py-0.5 rounded bg-white/10">
              {fieldType}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={copyToClipboard}
              className="p-2 rounded-lg hover:bg-white/10 text-white/60 
                         hover:text-white transition-colors"
              title="Copy to clipboard"
            >
              <CopyIcon />
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 text-white/60 
                         hover:text-white transition-colors"
              title="Close"
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 glass-scroll">
          {renderContent(value, fieldType)}
        </div>
      </div>
    </div>
  );
}

/**
 * Render content based on field type
 */
function renderContent(value: unknown, type: ParseFieldType): React.ReactNode {
  if (value === null) {
    return <span className="text-white/40 italic">null</span>;
  }
  if (value === undefined) {
    return <span className="text-white/40 italic">undefined</span>;
  }

  switch (type) {
    case 'String':
      return (
        <pre className="text-white/80 whitespace-pre-wrap font-mono text-sm leading-relaxed">
          {String(value)}
        </pre>
      );

    case 'Object':
    case 'Array':
    case 'Bytes':
      return (
        <pre className="text-white/80 whitespace-pre-wrap font-mono text-sm leading-relaxed 
                        bg-black/30 rounded-lg p-4 border border-white/5">
          {JSON.stringify(value, null, 2)}
        </pre>
      );

    case 'GeoPoint':
      return renderGeoPoint(value as ParseGeoPoint);

    case 'File':
      return renderFile(value as ParseFile);

    case 'ACL':
      return renderACL(value as ParseACL);

    case 'Polygon':
      return renderPolygon(value as ParsePolygon);

    case 'Date':
      return renderDate(value);

    default:
      return (
        <pre className="text-white/80 whitespace-pre-wrap font-mono text-sm">
          {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
        </pre>
      );
  }
}

/**
 * Render GeoPoint with map link
 */
function renderGeoPoint(geo: ParseGeoPoint) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-black/30 rounded-lg p-4 border border-white/5">
          <div className="text-white/40 text-xs mb-1 uppercase tracking-wide">Latitude</div>
          <div className="text-white font-mono text-lg">{geo.latitude}</div>
        </div>
        <div className="bg-black/30 rounded-lg p-4 border border-white/5">
          <div className="text-white/40 text-xs mb-1 uppercase tracking-wide">Longitude</div>
          <div className="text-white font-mono text-lg">{geo.longitude}</div>
        </div>
      </div>
      <a
        href={`https://www.google.com/maps?q=${geo.latitude},${geo.longitude}`}
        target="_blank"
        rel="noreferrer noopener"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg 
                   bg-[#208AEC]/20 text-[#208AEC] hover:bg-[#208AEC]/30 
                   transition-colors text-sm font-medium"
      >
        <MapIcon />
        Open in Google Maps
      </a>
    </div>
  );
}

/**
 * Render File with preview and download
 */
function renderFile(file: ParseFile) {
  const isImage = /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(file.name);
  
  return (
    <div className="space-y-4">
      {isImage && file.url && (
        <div className="bg-black/30 rounded-lg p-4 border border-white/5">
          <img
            src={file.url}
            alt={file.name}
            className="max-w-full max-h-64 rounded-lg object-contain mx-auto"
          />
        </div>
      )}
      <div className="flex items-center justify-between bg-black/30 rounded-lg p-4 border border-white/5">
        <div>
          <div className="text-white/40 text-xs mb-1 uppercase tracking-wide">Filename</div>
          <div className="text-white font-medium">{file.name}</div>
        </div>
        {file.url && (
          <a
            href={file.url}
            target="_blank"
            rel="noreferrer noopener"
            className="px-4 py-2 bg-[#208AEC] text-white rounded-lg text-sm 
                       font-medium hover:bg-[#208AEC]/80 transition-colors"
          >
            Download
          </a>
        )}
      </div>
    </div>
  );
}

/**
 * Render ACL as permission table
 */
function renderACL(acl: ParseACL) {
  const entries = Object.entries(acl);
  
  if (entries.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="text-red-400 font-medium mb-2">Master Key Only</div>
        <div className="text-white/40 text-sm">No public or role-based permissions</div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {entries.map(([key, perms]) => {
        let label = key;
        let icon = <UserIcon />;
        
        if (key === '*') {
          label = 'Public';
          icon = <GlobeIcon />;
        } else if (key.startsWith('role:')) {
          label = key.replace('role:', 'Role: ');
          icon = <ShieldIcon />;
        }

        return (
          <div 
            key={key}
            className="flex items-center justify-between bg-black/30 rounded-lg 
                       p-3 border border-white/5"
          >
            <div className="flex items-center gap-3">
              <span className="text-white/40">{icon}</span>
              <span className="text-white font-medium">{label}</span>
            </div>
            <div className="flex items-center gap-2">
              {perms.read && (
                <span className="px-2 py-1 rounded bg-emerald-500/20 text-emerald-400 text-xs">
                  Read
                </span>
              )}
              {perms.write && (
                <span className="px-2 py-1 rounded bg-blue-500/20 text-blue-400 text-xs">
                  Write
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Render Polygon coordinates
 */
function renderPolygon(polygon: ParsePolygon) {
  return (
    <div className="space-y-3">
      <div className="text-white/40 text-sm">
        {polygon.coordinates.length} vertices
      </div>
      <div className="bg-black/30 rounded-lg border border-white/5 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left px-4 py-2 text-white/40 font-medium">#</th>
              <th className="text-left px-4 py-2 text-white/40 font-medium">Latitude</th>
              <th className="text-left px-4 py-2 text-white/40 font-medium">Longitude</th>
            </tr>
          </thead>
          <tbody>
            {polygon.coordinates.map((coord, idx) => (
              <tr key={idx} className="border-b border-white/5 last:border-0">
                <td className="px-4 py-2 text-white/40">{idx + 1}</td>
                <td className="px-4 py-2 text-white font-mono">{coord[0]}</td>
                <td className="px-4 py-2 text-white font-mono">{coord[1]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Render Date with full details
 */
function renderDate(value: unknown) {
  let date: Date;
  
  if (typeof value === 'string') {
    date = new Date(value);
  } else if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'object' && value !== null && '__type' in value) {
    const dateObj = value as unknown as { iso: string };
    date = new Date(dateObj.iso);
  } else {
    return <span className="text-white/40 italic">Invalid date</span>;
  }

  if (isNaN(date.getTime())) {
    return <span className="text-white/40 italic">Invalid date</span>;
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-black/30 rounded-lg p-4 border border-white/5">
          <div className="text-white/40 text-xs mb-1 uppercase tracking-wide">ISO String</div>
          <div className="text-white font-mono text-sm">{date.toISOString()}</div>
        </div>
        <div className="bg-black/30 rounded-lg p-4 border border-white/5">
          <div className="text-white/40 text-xs mb-1 uppercase tracking-wide">UTC</div>
          <div className="text-white font-mono text-sm">{date.toUTCString()}</div>
        </div>
      </div>
      <div className="bg-black/30 rounded-lg p-4 border border-white/5">
        <div className="text-white/40 text-xs mb-1 uppercase tracking-wide">Local Time</div>
        <div className="text-white font-mono text-sm">{date.toLocaleString()}</div>
      </div>
    </div>
  );
}

// Icons
function CopyIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function MapIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}

